/**
 * The whole wire surface, in one place. Types come straight from the server so the two cannot
 * drift; `import type` means none of it survives into the bundle.
 */
import type {
	AccountLifecycle,
	AccountRole,
	AccountSummary,
	Basket,
	BasketLine,
	BonusWeek,
	BonusOverview,
	Category,
	CommitResult,
	ReceiptSyncResult,
	Recommendation,
	Trip,
	Tunables,
} from "../../server/types.ts";

export type Principal = {
	id: number | null;
	username: string;
	displayName: string;
	email: string | null;
	authMethod: "LOCAL" | "OIDC" | "NONE";
	csrfToken: string;
};

export type Bootstrap = {
	principal: Principal;
	week: BonusWeek;
	categories: Category[];
	tunables: Tunables;
	accounts: AccountSummary[];
};

/** Only `ok` is consumed; the rest of the health payload exists for humans reading curl output. */
export type Health = { ok: boolean };

export class ApiError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = "ApiError";
		this.status = status;
	}

	/** Authentik sits in front of the server, so a 401 means the browser session, not the app. */
	get unauthenticated(): boolean {
		return this.status === 401;
	}

	/** No response at all: aeroplane mode, dead reverse proxy, server not running. */
	get offline(): boolean {
		return this.status === 0;
	}
}

let authLost: (() => void) | null = null;
let csrfToken: string | null = null;

/** Registered once by the app shell so a 401 anywhere raises the re-authenticate banner. */
export function onAuthLost(handler: () => void): void {
	authLost = handler;
}

export function isAbort(err: unknown): boolean {
	return err instanceof DOMException && err.name === "AbortError";
}

export function errorMessage(err: unknown): string {
	if (err instanceof Error) return err.message;
	return String(err);
}

type Opts = { signal?: AbortSignal };

async function request<T>(
	method: string,
	path: string,
	body: unknown,
	opts: Opts | undefined,
	timeoutMs?: number,
): Promise<T> {
	const headers: Record<string, string> = { accept: "application/json" };
	if (body !== undefined) headers["content-type"] = "application/json";
	if (method !== "GET" && method !== "HEAD" && csrfToken !== null) {
		headers["x-csrf-token"] = csrfToken;
	}
	const init: RequestInit = {
		method,
		headers,
		// OIDC and local login both end in the app's own HttpOnly session cookie.
		credentials: "include",
		cache: "no-store",
	};
	if (body !== undefined) init.body = JSON.stringify(body);
	const deadline = timeoutMs === undefined ? null : AbortSignal.timeout(timeoutMs);
	if (deadline !== null) {
		init.signal = opts?.signal === undefined ? deadline : AbortSignal.any([opts.signal, deadline]);
	} else if (opts?.signal !== undefined) init.signal = opts.signal;

	let res: Response;
	let text: string;
	try {
		res = await fetch(path, init);
		text = res.status === 204 ? "" : await res.text();
	} catch (err) {
		if (opts?.signal?.aborted) throw err;
		if (deadline?.aborted) {
			throw new ApiError(408, "Solving took too long. Check your connection and try Solve again.");
		}
		if (isAbort(err)) throw err;
		throw new ApiError(0, `cannot reach the server (${errorMessage(err)})`);
	}

	let payload: unknown = null;
	if (text !== "") {
		try {
			payload = JSON.parse(text) as unknown;
		} catch {
			payload = null;
		}
	}

	if (!res.ok) {
		if (res.status === 401) authLost?.();
		let detail: string | null = null;
		if (typeof payload === "object" && payload !== null && "error" in payload) {
			const value = payload.error;
			if (typeof value === "string" && value !== "") detail = value;
		}
		throw new ApiError(res.status, detail ?? `${method} ${path} failed with ${res.status}`);
	}
	if (res.status !== 204 && payload === null) {
		throw new ApiError(res.status, `${method} ${path} returned no valid JSON. Try again or sign in again.`);
	}
	return payload as T;
}

const get = <T>(path: string, opts?: Opts) => request<T>("GET", path, undefined, opts);
const post = <T>(path: string, body?: unknown, opts?: Opts) => request<T>("POST", path, body, opts);
const put = <T>(path: string, body: unknown, opts?: Opts) => request<T>("PUT", path, body, opts);
const patch = <T>(path: string, body: unknown, opts?: Opts) => request<T>("PATCH", path, body, opts);
const del = <T>(path: string, opts?: Opts) => request<T>("DELETE", path, undefined, opts);

export const api = {
	health: (o?: Opts) => get<Health>("/api/health", o),
	authConfig: (o?: Opts) =>
		get<{ oidc: boolean; local: boolean; registration: false }>("/api/auth/config", o),
	login: (username: string, password: string, o?: Opts) =>
		post<{ ok: true }>("/api/auth/login", { username, password }, o),
	logout: (o?: Opts) => post<{ ok: true }>("/api/auth/logout", undefined, o),
	bootstrap: async (o?: Opts): Promise<Bootstrap> => {
		const boot = await get<Bootstrap>("/api/bootstrap", o);
		csrfToken = boot.principal.csrfToken;
		return boot;
	},

	bonusOverview: (refresh = false, o?: Opts) =>
		get<BonusOverview>(`/api/bonus-overview${refresh ? "?refresh=1" : ""}`, o),
	createBasket: (o?: Opts) => post<Basket>("/api/baskets", undefined, o),
	basket: (id: number, o?: Opts) => get<Basket>(`/api/baskets/${id}`, o),
	scan: (id: number, barcode: string, qty?: number, o?: Opts) =>
		post<{ basket: Basket; line: BasketLine | null }>(
			`/api/baskets/${id}/scan`,
			qty === undefined ? { barcode } : { barcode, qty },
			o,
		),
	setLineQty: (id: number, lineId: number, qty: number, o?: Opts) =>
		patch<Basket>(`/api/baskets/${id}/lines/${lineId}`, { qty }, o),
	removeLine: (id: number, lineId: number, o?: Opts) => del<Basket>(`/api/baskets/${id}/lines/${lineId}`, o),
	setBasketCategory: (id: number, categoryId: number | null, o?: Opts) =>
		put<Basket>(`/api/baskets/${id}/category`, { categoryId }, o),
	recommendation: async (id: number, o?: Opts): Promise<Recommendation> => {
		const rec = await request<Recommendation>("GET", `/api/baskets/${id}/recommendation`, undefined, o, 120_000);
		if (rec?.basketId !== id) {
			throw new ApiError(502, "The server returned an invalid recommendation. Try Solve again.");
		}
		return rec;
	},
	commit: (id: number, accountId: number | undefined, o?: Opts) =>
		post<CommitResult>(`/api/baskets/${id}/commit`, accountId === undefined ? {} : { accountId }, o),

	categories: (o?: Opts) => get<Category[]>("/api/categories", o),
	createCategory: (name: string, o?: Opts) => post<Category>("/api/categories", { name }, o),
	renameCategory: (id: number, name: string, o?: Opts) => patch<Category>(`/api/categories/${id}`, { name }, o),
	archiveCategory: (id: number, archived: boolean, o?: Opts) =>
		patch<Category>(`/api/categories/${id}`, { archived }, o),
	deleteCategory: (id: number, o?: Opts) => del<void>(`/api/categories/${id}`, o),
	mergeCategory: (id: number, intoId: number, o?: Opts) =>
		post<Category>(`/api/categories/${id}/merge`, { intoId }, o),

	accounts: (o?: Opts) => get<AccountSummary[]>("/api/accounts", o),
	/** `role: null` removes the link entirely. */
	setLink: (id: number, categoryId: number, role: AccountRole | null, pinned: boolean, o?: Opts) =>
		put<AccountSummary>(`/api/accounts/${id}/links`, { categoryId, role, pinned }, o),
	setLifecycle: (id: number, state: AccountLifecycle, o?: Opts) =>
		put<AccountSummary>(`/api/accounts/${id}/lifecycle`, { state }, o),
	refreshAccount: (id: number, o?: Opts) => post<AccountSummary>(`/api/accounts/${id}/refresh`, undefined, o),

	trips: (limit: number, offset: number, o?: Opts) => get<Trip[]>(`/api/trips?limit=${limit}&offset=${offset}`, o),
	trip: (id: number, o?: Opts) => get<Trip>(`/api/trips/${id}`, o),
	setRealised: (id: number, cents: number, o?: Opts) => put<Trip>(`/api/trips/${id}/realised`, { cents }, o),

	syncReceipts: (o?: Opts) => post<ReceiptSyncResult>("/api/receipts/sync", undefined, o),

	setTunables: (t: Tunables, o?: Opts) => put<Tunables>("/api/tunables", t, o),
};
